// src/components/Dashboard_ADM/Atendimento/LeadsTab.tsx
// Lista de leads com filtros por nicho/estado/data — seção 8.2. GET /whatsapp-agent/leads

import React, { useState, useEffect, useCallback } from 'react';
import { Users, Loader2, RefreshCw, Search } from 'lucide-react';
import apiClient from '../../../services/api';
import { LeadRow, AGENT_LABELS, NICHO_AGENTS, formatDateTime } from './shared';

const ESTADOS_BR = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR',
  'PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const FUNNEL_STAGE_COLORS: Record<string, string> = {
  'Contato': 'bg-gray-100 text-gray-700',
  'Em Análise': 'bg-amber-100 text-amber-700',
  'Aprovado': 'bg-blue-100 text-blue-700',
  'Pagamento Enviado': 'bg-indigo-100 text-indigo-700',
  'Pago': 'bg-emerald-100 text-emerald-700',
  'Ativo': 'bg-green-100 text-green-700',
};

const LeadsTab: React.FC = () => {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroNicho, setFiltroNicho] = useState('');
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroEstado) params.set('state', filtroEstado);
      if (filtroNicho) params.set('nicho', filtroNicho);
      if (filtroDe) params.set('from', filtroDe);
      if (filtroAte) params.set('to', filtroAte);
      const data = await apiClient.get<LeadRow[]>(`/whatsapp-agent/leads?${params.toString()}`);
      setLeads(data ?? []);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroNicho, filtroDe, filtroAte]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const leadsFiltrados = leads.filter((l) => {
    if (!busca) return true;
    const alvo = `${l.schoolName} ${l.responsibleName ?? ''} ${l.whatsapp ?? ''}`.toLowerCase();
    return alvo.includes(busca.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <Users className="w-6 h-6 text-blue-600" /> Leads
            </h2>
            <p className="text-gray-600">Todos os leads capturados pelo agente, com etapa do funil e nicho</p>
          </div>
          <button onClick={fetchLeads}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, responsável ou WhatsApp…"
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-semibold" />
          </div>
          <select value={filtroNicho} onChange={(e) => setFiltroNicho(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
            <option value="">Todos os Nichos</option>
            {NICHO_AGENTS.map((id) => <option key={id} value={id}>{AGENT_LABELS[id]}</option>)}
          </select>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
            <option value="">Todos os Estados</option>
            {ESTADOS_BR.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </select>
          <input type="date" value={filtroDe} onChange={(e) => setFiltroDe(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando leads…</p>
          </div>
        ) : leadsFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-black text-lg">Nenhum lead encontrado</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {leadsFiltrados.map((lead) => (
              <div key={lead.affiliateId} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="font-bold text-gray-900">{lead.schoolName}</p>
                  {lead.state && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">{lead.state}</span>
                  )}
                  {lead.currentAgent && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
                      {AGENT_LABELS[lead.currentAgent]}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${FUNNEL_STAGE_COLORS[lead.funnelStage] ?? 'bg-gray-100 text-gray-700'}`}>
                    {lead.funnelStage}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{lead.responsibleName || '—'} · {lead.whatsapp || 'sem WhatsApp'}</p>
                <p className="text-xs text-gray-400 mt-1">Cadastrado em {formatDateTime(lead.createdAt)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsTab;
